// Cache Management Service
// Handles localStorage cache clearing and management

/**
 * List of all localStorage keys used by the app
 */
const CACHE_KEYS = [
  // Calendar and scheduling
  'calendar_weeks',
  'scheduled_movies',
  'archivedWeeks',
  
  // Reviews and ratings
  'movie_reviews',
  'movie_viewers',
  'starred_movies',
  
  // App-level data
  'predefined_names',
  'movie_statuses',
  'movieStatuses', // Legacy key
  'survey_email_users',
  'week_visibility',
  
  // Survey data
  'survey_votes',
  
  // Movie collection
  'movie_collection',
  
  // Media and settings
  'countdown_images',
  'admin_default_tab',
  'admin_debug_mode',
  
  // TMDB cache
  'tmdb_api_key',
  'tmdb_bearer_token',
  'tmdb_cache_memory',
  
  // User preferences (pattern-based)
  // These will be handled separately since they have dynamic keys
];

/**
 * Clear all app-related localStorage cache
 */
export const clearAllCache = () => {
  try {
    console.log('🧹 Clearing all localStorage cache...');
    
    let clearedCount = 0;
    
    // Clear known cache keys
    CACHE_KEYS.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedCount++;
        console.log(`✅ Cleared cache: ${key}`);
      }
    });
    
    // Clear pattern-based keys (starred movies with user identifiers)
    const allKeys = Object.keys(localStorage);
    const patternKeys = allKeys.filter(key => 
      key.startsWith('starred_movies_') ||
      key.startsWith('user_profile_') ||
      key.startsWith('tmdb_cache_')
    );
    
    patternKeys.forEach(key => {
      localStorage.removeItem(key);
      clearedCount++;
      console.log(`✅ Cleared pattern cache: ${key}`);
    });
    
    console.log(`✅ Cache cleared successfully! Removed ${clearedCount} items.`);
    return { success: true, clearedCount };
    
  } catch (error) {
    console.error('❌ Error clearing cache:', error);
    return { success: false, error };
  }
};

/**
 * Clear specific cache categories
 */
export const clearCacheCategory = (category) => {
  try {
    console.log(`🧹 Clearing ${category} cache...`);
    
    const categoryKeys = {
      calendar: ['calendar_weeks', 'scheduled_movies', 'archivedWeeks'],
      reviews: ['movie_reviews', 'movie_viewers'],
      starred: ['starred_movies'],
      survey: ['survey_votes', 'survey_email_users'],
      collection: ['movie_collection'],
      settings: ['predefined_names', 'movie_statuses', 'movieStatuses', 'week_visibility'],
      media: ['countdown_images'],
      tmdb: ['tmdb_api_key', 'tmdb_bearer_token', 'tmdb_cache_memory']
    };
    
    const keys = categoryKeys[category];
    if (!keys) {
      throw new Error(`Unknown cache category: ${category}`);
    }
    
    let clearedCount = 0;
    keys.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedCount++;
        console.log(`✅ Cleared ${category} cache: ${key}`);
      }
    });
    
    // Handle pattern-based keys for starred movies
    if (category === 'starred') {
      const allKeys = Object.keys(localStorage);
      const starredKeys = allKeys.filter(key => key.startsWith('starred_movies_'));
      starredKeys.forEach(key => {
        localStorage.removeItem(key);
        clearedCount++;
        console.log(`✅ Cleared starred cache: ${key}`);
      });
    }
    
    console.log(`✅ ${category} cache cleared! Removed ${clearedCount} items.`);
    return { success: true, clearedCount };
    
  } catch (error) {
    console.error(`❌ Error clearing ${category} cache:`, error);
    return { success: false, error };
  }
};

/**
 * Get cache status and sizes
 */
export const getCacheStatus = () => {
  try {
    const status = {
      totalItems: 0,
      totalSize: 0,
      categories: {}
    };
    
    const categoryKeys = {
      calendar: ['calendar_weeks', 'scheduled_movies', 'archivedWeeks'],
      reviews: ['movie_reviews', 'movie_viewers'],
      starred: ['starred_movies'],
      survey: ['survey_votes', 'survey_email_users'],
      collection: ['movie_collection'],
      settings: ['predefined_names', 'movie_statuses', 'movieStatuses', 'week_visibility'],
      media: ['countdown_images'],
      tmdb: ['tmdb_api_key', 'tmdb_bearer_token', 'tmdb_cache_memory']
    };
    
    Object.entries(categoryKeys).forEach(([category, keys]) => {
      status.categories[category] = {
        items: 0,
        size: 0,
        keys: []
      };
      
      keys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value !== null) {
          const size = new Blob([value]).size;
          status.categories[category].items++;
          status.categories[category].size += size;
          status.categories[category].keys.push(key);
          status.totalItems++;
          status.totalSize += size;
        }
      });
    });
    
    // Check pattern-based keys
    const allKeys = Object.keys(localStorage);
    const patternKeys = allKeys.filter(key => 
      key.startsWith('starred_movies_') ||
      key.startsWith('user_profile_') ||
      key.startsWith('tmdb_cache_')
    );
    
    if (patternKeys.length > 0) {
      status.categories.patterns = {
        items: patternKeys.length,
        size: 0,
        keys: patternKeys
      };
      
      patternKeys.forEach(key => {
        const value = localStorage.getItem(key);
        if (value !== null) {
          const size = new Blob([value]).size;
          status.categories.patterns.size += size;
          status.totalItems++;
          status.totalSize += size;
        }
      });
    }
    
    return status;
    
  } catch (error) {
    console.error('❌ Error getting cache status:', error);
    return null;
  }
};

/**
 * Format bytes to human readable string
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
